import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ToastContainer, toast, Slide } from 'react-toastify';
import update from 'immutability-helper';
import Interview from './Interview';
import './ProposalInterviews.scss';

const DEFAULT_INTERVIEW_LEN = 4;

const INTERVIEW_TYPE_ORDER = ['upcoming', 'pending', 'reschedule', 'past'];

class ProposalInterviews extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentlyViewingMore: false,
      upcoming: this.props.interviews.upcoming,
      pending: this.props.interviews.pending,
      past: this.props.interviews.past,
      reschedule: this.props.interviews.reschedule,
    };

    this.handleViewMore = this.handleViewMore.bind(this);
    this.removeInterview = this.removeInterview.bind(this);
    this.handleInterviewMeetingDetailsSubmit = this.handleInterviewMeetingDetailsSubmit.bind(this);
  }

  getInterviews(type) {
    const { currentlyViewingMore } = this.state;
    if (currentlyViewingMore) {
      return this.state[type];
    }

    const typeIndex = INTERVIEW_TYPE_ORDER.findIndex((t) => type === t);
    let priorInterviewsCount = 0;
    for (let ii = 0; ii < typeIndex; ii += 1) {
      priorInterviewsCount += this.state[INTERVIEW_TYPE_ORDER[ii]].length;
    }

    const spaceLeft = DEFAULT_INTERVIEW_LEN - priorInterviewsCount;

    // It is ok for slice to overshoot array bounds, but not undershoot (in this context).
    // Slice interprets negative numbers as an offset from the end.
    // But, if there is negative spaceLeft, then we don't want to return any interviews...
    // So, special case to return empty array if spaceLeft is negative.
    if (spaceLeft <= 0) {
      return [];
    }

    const interviewsOfType = this.state[type];
    return interviewsOfType.slice(0, spaceLeft);
  }

  removeInterview(id, response, type = 'upcoming') {
    const interviews = this.state[type];
    const newInterviews = interviews.filter((interview) => interview.id !== id);
    const toastMessage = response.data.message;
    const { consultantView } = this.props;
    const rescheduleLink = `${window.location.href}?show_interview_modal=true`;

    this.setState({ [type]: newInterviews });
    toast.success(
      <span>
        {toastMessage}
        {!consultantView
          && (
          <span>
            &nbsp;
            Reschedule
            {' '}
            <a className="reschedule-interview-link" href={rescheduleLink}>here.</a>
          </span>
          )}
      </span>,
    );
  }

  handleInterviewMeetingDetailsSubmit(response) {
    const { id } = response.data;
    const newMeetingDetails = response.data.meeting_details;
    const oldInterviews = this.state[response.data.type];

    const oldInterviewIndex = oldInterviews.findIndex((interview) => interview.id === id);

    const newInterviews = update(oldInterviews, {
      [oldInterviewIndex]: {
        meeting_details: { $set: newMeetingDetails },
      },
    });

    const setStateArg = { [response.data.type]: newInterviews };
    this.setState(setStateArg);
  }

  handleViewMore(currentlyViewingMore) {
    this.setState({ currentlyViewingMore });
  }

  renderHeader() {
    let copy;
    if (this.props.consultantView) {
      copy = 'My interviews for this project';
    } else {
      copy = `${this.props.consultantName}'s interviews`;
    }

    return (
      <div className="interviews-header medium-weight body-text-1  mb-3 ">
        <i className="fas fa-calendar-alt me-2" />
        {copy}
      </div>
    );
  }

  renderInterviews(type) {
    const {
      possibleInterviewers,
      invitableRoles,
      csrfToken,
      canCancel,
      canEdit,
      consultantView,
      consultantInterviewPath,
      proposalId,
      isRep,
      repOrOwner,
      newInterviewSlot,
      availableInterviewSlotUrl,
    } = this.props;
    const interviews = this.getInterviews(type);

    return (
      <Fragment>
        <div className={`${type}-interviews-list-container  mb-3 `}>
          {interviews.map((interview) => (
            <Interview
              key={interview.id}
              interviewType={type}
              interview={interview}
              possibleInterviewers={possibleInterviewers}
              invitableRoles={invitableRoles}
              onCancel={this.removeInterview}
              onSubmitMeetingDetails={this.handleInterviewMeetingDetailsSubmit}
              isRep={isRep}
              canCancel={canCancel}
              canEdit={canEdit}
              consultantView={consultantView}
              csrfToken={csrfToken}
              consultantInterviewPath={consultantInterviewPath}
              proposalId={proposalId}
              repOrOwner={repOrOwner}
              newInterviewSlot={newInterviewSlot}
              availableInterviewSlotUrl={availableInterviewSlotUrl}
            />
          ))}
        </div>
        { interviews.length > 0 && <hr className="interview-list-divider" /> }
      </Fragment>
    );
  }

  renderViewMore() {
    const { currentlyViewingMore } = this.state;
    const ctaCopy = currentlyViewingMore ? 'View Less' : 'View All';
    const totalInterviewCount = this.state.upcoming.length
      + this.state.past.length
      + this.state.pending.length;

    if (totalInterviewCount <= DEFAULT_INTERVIEW_LEN) {
      return null;
    }

    return (
      <div className="interview-view-all-link-container">
        <a
          role="button"
          tabIndex="0"
          className="interview-view-all-link body-text-2 medium-weight"
          onClick={() => this.handleViewMore(!currentlyViewingMore)}
        >
          {ctaCopy}
        </a>
      </div>
    );
  }

  render() {
    return (
      <div className="interviews-container">
        <ToastContainer
          position="bottom-center"
          transition={Slide}
          hideProgressBar
          autoClose={8000}
          closeOnClick
          draggable={false}
          pauseOnHover
          pauseOnFocusLoss
          className="cancelled-interview-toast-container"
          toastClassName="cancelled-interview-toast"
          bodyClassName="cancelled-interview-toast-body"
        />
        {this.renderHeader()}
        <div className="interviews">
          {INTERVIEW_TYPE_ORDER.map((type) => (
            <div key={type} id={type}>
              {this.renderInterviews(type)}
            </div>
          ))}
        </div>
        {this.renderViewMore()}
      </div>
    );
  }
}

ProposalInterviews.propTypes = {
  interviews: PropTypes.shape({
    upcoming: PropTypes.arrayOf(PropTypes.shape({})),
    past: PropTypes.arrayOf(PropTypes.shape({})),
    pending: PropTypes.arrayOf(PropTypes.shape({})),
    reschedule: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  consultantName: PropTypes.string.isRequired,
  possibleInterviewers: PropTypes.shape({}),
  invitableRoles: PropTypes.arrayOf(PropTypes.string),
  isRep: PropTypes.bool,
  canCancel: PropTypes.bool,
  canEdit: PropTypes.bool,
  consultantView: PropTypes.bool,
  repOrOwner: PropTypes.bool,
  csrfToken: PropTypes.string.isRequired,
  consultantInterviewPath: PropTypes.string,
  proposalId: PropTypes.number.isRequired,
  newInterviewSlot: PropTypes.bool,
  availableInterviewSlotUrl: PropTypes.string.isRequired,
};

ProposalInterviews.defaultProps = {
  possibleInterviewers: [],
  invitableRoles: [],
  isRep: false,
  canCancel: false,
  canEdit: false,
  consultantView: false,
  repOrOwner: false,
  consultantInterviewPath: '',
  newInterviewSlot: false,
};

export default ProposalInterviews;
